import { Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { firstValueFrom, timeout } from 'rxjs';
import { AuthService, accountMatchesPolicy } from './auth.service';
import { environment } from '../../environments/environment';
import { APP_STORAGE_KEYS } from '../constants';
import { UserService } from './user.service';

@Injectable({ providedIn: 'root' })
export class SessionRecoveryService {
  private static readonly RECOVERY_KEY = 'cm_recovery_attempt';
  private inFlight = false;
  static readonly RECOVERABLE_OUTCOMES = new Set([
    'brand_not_found', 'identity_not_eligible', 'application_mismatch',
    'multiple_matching_accounts', 'auth_required', 'token_invalid', 'session_update_required',
  ]);
  constructor(private msal: MsalService, private user: UserService, private auth: AuthService) {}
  hasRecoveryAttempt(): boolean { return sessionStorage.getItem(SessionRecoveryService.RECOVERY_KEY) === '1'; }
  /** Called only after business identity, sidebar, questionnaire and navigation succeed. */
  clearRecoveryMarker(): void { sessionStorage.removeItem(SessionRecoveryService.RECOVERY_KEY); }

  async executeSignInAgain(): Promise<void> {
    if (this.inFlight || this.hasRecoveryAttempt()) return;
    this.inFlight = true;
    sessionStorage.setItem(SessionRecoveryService.RECOVERY_KEY, '1');
    try {
      const policy = environment.policies.signUpSignIn.toLowerCase();
      const matches = this.msal.instance.getAllAccounts().filter(account =>
        accountMatchesPolicy(account, policy));
      const active = this.msal.instance.getActiveAccount();
      const selected = matches.find(account => account.homeAccountId === active?.homeAccountId
        && account.localAccountId === active?.localAccountId
        && account.tenantId === active?.tenantId && account.environment === active?.environment) ?? (matches.length === 1 ? matches[0] : undefined);
      // Await the backend cookie deletion before touching the selected account's cache.
      // A failure stops recovery and leaves the one-shot marker in place.
      await firstValueFrom(this.user.deleteUserCookies().pipe(timeout(5000)));
      this.auth.invalidateSession();
      APP_STORAGE_KEYS.forEach(key => localStorage.removeItem(key));
      // Never invoke clearCache without an account: that clears unrelated accounts.
      // With several product-policy accounts and no matching active one, clearing only a "selected"
      // account cleared nothing; the next sign-in stayed ambiguous until the browser storage was wiped.
      // Every account of this product's policy is ours to clear; other policies are left untouched.
      const toClear = selected ? [selected] : matches;
      for (const account of toClear) await this.msal.instance.clearCache({ account });
      if (toClear.some(account => account.homeAccountId === active?.homeAccountId)) this.msal.instance.setActiveAccount(null);
      // Task 1 must supply evidence before any additional stale artifact is removed.
      await this.auth.beginInteractiveSignIn();
    } finally { this.inFlight = false; }
  }
}
